<template>
  <div>
      <div class="title"> N Pendulum </div>
      <div class="date"> August 17th, 2019 </div>

      <div class="content">
	<div id="pageContainer">
        <div id="canvasContainer" style="border: 1px solid gainsboro; position: relative;">
            <div id="progressContainer" style="text-align:center; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);">
                <div id="progressText">0 %</div>
                <progress id="progressBar" value="25" max="100"></progress>
                <div>This may take a while...</div>
            </div>
        </div>

        <div style="display: flex; justify-content: center; padding: 5px">
            <button id="pathTraceButton" class="customButton" style="margin: 5px">Trace Path</button>
            <button id="resetButton" class="customButton" style="margin: 5px">Reset</button>
            <button id="increaseButton" class="customButton" style="margin: 5px">Increase size</button>
            <button id="decreaseButton" class="customButton" style="margin: 5px">Decrease size</button>
            <button id="randomButton" class="customButton" style="margin: 5px">Change to simple start</button>
        </div>

        <p>
            Simulates the first 10 seconds of a pendulum system of any size `N`, assuming no air resistence, friction,
            with massless, permantently taut strings. The animation is slowed down to ~40 seconds in length (could be much slower
            for a slower computer) 
        </p>
        <p>Glad I am finally able to complete this. Implemented using pure Javascript (yikes). I created this because I
            wanted to see how the pendulum behaved for a larger system.
        </p>
        <p>To implement this, I spent a long time creating my own slow algorithm to solve a system of linear equations, then
            scrapping that algorithm and opting for the much faster Gaussian Elimination algorithm. This animation is only possible
            because all of the unknown variables within the equations of motion are linear, allowing us to solve for them.
        </p>

        <button id="nerd_button" class="customButton">Show me the math!</button>

        <div id="what_a_nerd" style="display: none">
            <p style="font-weight: bold">The physics behind it and solution (for my future self who comes back to this)</p>
            <p>To make things simple, let us set the origin of the coordinate system to be
                at the very top from where the pendulum hangs. Let `\hat{i} = (1, 0)` and `\hat{j} = (0, -1)`. So positive `y` is
                actually downwards, which makes things easier. A picture to understand the following equations.
            </p>
            <img style="width: 30%; display: block; margin: 0 auto;" src="static/n_pendulum/pendulum.png" alt="Pendulum Diagram">
            
            <p>
                To describe the position of each pendulum bob is pretty simple. It is just the position of the last pendulum plus
                a distance `L` at some angle `\theta`. Let the subscript indicate which pendulum bob it is.
            </p>
            <p style="text-align: center">
                `x_1 = L_1 \sin{\theta_1} \quad y_1 = L_1 \cos{\theta_1}`
                <br/>
                `x_2 = x_1 + L_2 \sin{\theta_2} \quad y_2 = y_1 + L_2 \cos{\theta_2}`
                <br/>
                `x_n = x_{n-1} + L_n \sin{\theta_n} \quad y_n = y_{n-1} + L_n \cos{\theta_n }`
                <br/>
            </p>
            <p>
                Taking the derivative of these equations twice will give us the following, accelerations `a`, 
                with angular velocity `\dot{\theta}`, and angular acceleration `\ddot{\theta}`
            </p>
            <p style="text-align: center">
                    `a_{x_n} = a_{x_{n-1}} + L_n (\ddot{\theta}_n \text{cos(} \theta_n \text{)} - \dot{\theta}_n^2\text{sin(} \theta_n \text{)} )`
                    <br/>
                    `a_{y_n} = a_{y_{n-1}} - L_n (\ddot{\theta}_n \text{sin(} \theta_n \text{)} + \dot{\theta}_n^2 \text{cos(} \theta_n \text{)} )`
            </p>
            <p>
                In the above acceleration, note that for `a_{x_1}` and `a_{y_1}`, `a_{x_{n-1}}` and `a_{y_{n-1}}` are 0.
                In other words, the first pendulum bob has no previous acceleration it is dependent on.
            </p>
            <p>
                Now, we know there are 2 forces acting on the pendulum bob: gravity and tension (`m_n*g` and `T_n` respectively).
                Now we derive the net forces on each bob.
            </p>        
            <div style="display: flex; justify-content: center; align-items: center; padding: 10px">
                    <img style="width:20%; margin: 10px" src="static/n_pendulum/forces1.png" alt="Forces Diagram 1">
                    <div>
                        <img style="width:40%; margin: 10px" src="static/n_pendulum/forces2.png" alt="Forces Diagram 2">
                    </div>
            </div>
            <p style="text-align: center">
                `F_{\text{net}, x_{n}} = T_{n+1} \text{sin(} \theta_{n+1} \text{)} - T_n \text{sin(} \theta_n \text{)} = m_n a_{x_n}`
                <br/>  
                <br/>  
                `F_{\text{net}, y_{n}} = m_ng + T_{n+1} \text{cos(} \theta_{n+1} \text{)} - T_n \text{cos(} \theta_n \text{)} = m_n a_{y_n}` 
            </p>
            <p>
                Note that for the last pendulum (the `N`th pendulum), `T_{n+1} = 0`
            </p>

            All in all, there are 4 equations for each pendulum bob, and 4 unknowns: `\ddot{\theta_n}, T_n, a_{x_n}, a_{y_n}`.
            And because every one of these unknowns is linear within these equations, we can solve for them like we solve for any linear system.
            For example, let us simulate a naive approach to solve linear systems using Cramer's rule, which is computationally slow.
            Suppose we have 3 equations and 3 unknowns.
            <a class="visibleLink" href="https://en.wikipedia.org/wiki/Cramer%27s_rule">Cramer's rule gives us an explicit solution, using determinants</a>:
            <br/>  
            <br/>  
            <img style="width: 20%; display: block; margin: 0 auto;" src="static/n_pendulum/cramers_rule1.svg" alt="Cramer's rule 1">
            <br/>  
            <img style="width: 50%; display: block; margin: 0 auto;" src="static/n_pendulum/cramers_rule2.svg" alt="Cramer's rule 2">
            <br/>  
            
            We can do the same thing here to solve for the pendulum system, if we keep track of all the `\theta_n`, `\ddot{\theta_n}`, `L_n`, `m_n`.
            Then we can plug in these known values into our derived equations, and make them a linear system of size `4N` where `N` is the number of pendulums.
            For example, I first hardcoded this for a double pendulum system, using the same equations (but variables moved around). For a double pendulum system,
            there are 8 equations.
            
            <!-- m_1a_{x_1} + T_1\text{sin(}\theta_1\text{)} - T_2\text{sin(}\theta_2\text{)} &&= 0    \\
            m_1a_{y_1} + T_1\text{cos(}\theta_1\text{)} - T_2\text{sin(}\theta_2\text{)} &&= m_2g \\
            m_2a_{x_2} + T_2\text{sin(}\theta_2\text{)} &&= 0    \\
            m_2a_{y_2} + T_2\text{cos(}\theta_2\text{)} &&= m_2g \\
            a_{x_2}-a_{x_1} - \ddot{\theta}_2 L_2 \text{cos(}\theta_2 \text{)} &&= - \dot{\theta}_2^2 L_2  \text{sin(}\theta_2\text{)} \\
            a_{y_2}-a_{y_1} + \ddot{\theta}_2 L_2 \text{sin(}\theta_2 \text{)} &&= - \dot{\theta}_2^2 L_2  \text{cos(}\theta_2\text{)} \\
            a_{x_1} - \ddot{\theta}_1 L_1 \text{cos(}\theta_1 \text{)} &&= - \dot{\theta}_1^2 L_1 \text{sin(}\theta_1\text{)} \\
            a_{y_1} + \ddot{\theta}_1 L_1 \text{sin(}\theta_1 \text{)} &&= - \dot{\theta}_1^2 L_1 \text{cos(}\theta_1\text{)} \\  -->
            
            <br/>  
            <br/>  
            <img style="width: 40%; display: block; margin: 0 auto;" src="static/n_pendulum/monster_equations.png" alt="Pendulum Diagram">
            <br/>  
            
            <p>
                It's overwhelming, but these equations can be generalized for any `N`. The only thing left to do to simulate a pendulum is create
                initial conditions for every single `\theta_n`, `\dot{\theta}_n`, `m_n`, and `L_n`. We plug everything into the equations and are left with
                a linear system which we can then use Cramer's rule to solve for all of the `\ddot{\theta_n}`. Using Euler's method, its simple enough to
                approximate `\theta_n` and thus simulate the pendulum.
            </p>
            
            <p>
                In reality, I used <a class="visibleLink" href="https://www.youtube.com/watch?v=qhF59G5OTng">Gaussian elimination</a> as the algorithm to
                solve linear systems because it is much faster. I talked about Cramer's rule here because the explicit solution is easier to describe.
            </p>
        </div>
        
        <br/>  
        <br/>  
        <br/>  
        <br/>  
        <br/>  
        <br/>  
        </div>

      </div>
  </div>
</template>

<script>

export default {
  name: 'NPendulum',
  mounted: () => {
    const script = function (p5) {
        p5.setup = () => {
            window.MathJax.typeset();

            let canvas_container = document.getElementById("canvasContainer");
            let canvas_width = canvas_container.offsetWidth;
            let canvas_height = 600;
            let canvas = p5.createCanvas(canvas_width, canvas_height);
            canvas.parent("canvasContainer");
            p5.fill(255, 0, 0);
            do_js(p5, canvas_width, canvas_height);
        }
    }

    const P5 = require('p5');
    new P5(script);
  }
}

function do_js(p5, canvas_width) {
class Vec {
    constructor(components) {
        if (!Array.isArray(components))
            throw `Invalid argument to Vec constructor (it should be an array, but it was not)`;
        this.components = components;
    }

    get x() {
        return this.components[0];
    }

    get y() {
        return this.components[1];
    }
    
    get z() {
        return this.components[2];
    }

    get length() {
        return this.components.length;
    }

    static cross(...vec) {
        // Generalized cross product
        // Given a list of vectors, 
        // returns a vector which is perpendicular to all given vectors 
        // and also has the length equal to the n-volume formed by the given vectors

        if (vec.length + 1 != vec[0].length)
            throw `Unable to do cross product given ${vec.length}, ${vec[0].length}-dimensional vectors`

        let matrix = [];
        // Fill first row with 1's we wont use it anyways
        for (let i = 0; i < vec[0].length; i++)
            matrix.push(1);
        
        // Each next row is the sequence of vectors
        for (let v of vec)
            matrix.push(...(v.components));

        let mat = new Matrix(vec[0].length, vec[0].length, matrix);
            
        let new_vec = [];
        for (let i = 0; i < vec[0].length; i++) {
            new_vec.push(
                ((i % 2 == 0) ? 1 : -1) * mat.__scratch_matrix(0, i).det()
            );
        }

        return new Vec(new_vec);
    }

    dot(vec2) {
        let sum = 0;
        for (let i = 0; i < Math.min(this.length, vec2.length); i++)
            sum += this.components[i] * vec2.components[i];
        return sum;
    }

    magnitude() {
        return Math.sqrt(this.dot(this));
    }
    
    direction() {
        let m = this.magnitude();
        if (m == 0)
            throw `Attempting to get direction of 0 length vector`;
        return this.multiply_scalar(1 / m);
    }
    
    multiply_scalar(scalar) {
        let copy = this.copy();
        for (let i = 0; i < copy.components.length; i++)
            copy.components[i] *= scalar;
        return copy;
    }
    
    add(vec2) {
        let components = [];
        for (let i = 0; i < Math.min(this.components.length, vec2.components.length); i++)
            components[i] = this.components[i] + vec2.components[i];
        return new Vec(components);
    }
    
    subtract(vec2) {
        let components = [];
        for (let i = 0; i < Math.min(this.components.length, vec2.components.length); i++)
            components[i] = this.components[i] - vec2.components[i];
        return new Vec(components);
    }
    
    copy() {
        return new Vec(this.components.slice(0));
    }

    to_array() {
        return this.components.slice(0);
    }

    equals(v2) {
        if (this.length != v2.length)
            return false;
        
        for (let i = 0; i < this.length; i++)
            if (this.components[i] != v2.components[i])
                return false;
        return true;
    }
}

class Matrix {
    // We use 1D array row major order to represent a matrix
    constructor(rows, cols, array) {
        this.array = array;
        this.rows = rows;
        this.cols = cols;

        if (array == undefined || array == null) {
            this.array = [];
            for (let i = 0; i < rows * cols; i++)
                this.array.push(0);
        }
    }

    copy() {
        return new Matrix(this.rows, this.cols, this.array.slice());
    }

    item(row, col) {
        return this.array[this.cols * row + col];
    }

    set(row, col, item) {
        this.array[this.cols * row + col] = item;
    }

    multiply_scalar(scalar) {
        let copy = this.copy();
        for (let i = 0; i < copy.array.length; i++)
            copy.array[i] *= scalar;
        return copy;
    }

    multiply_mat(m2) {
        if (this.cols != m2.rows)
            throw "Cannot multiply matrices without matching rows and columns";

        // Initialize empty array with amount of rows as 1st, but columns of 2nd
        let array = [];
        for (let i = 0; i < this.rows * m2.cols; i++)
            array.push(0);

        for (let j = 0; j < this.rows; j++) 
            for (let i = 0; i < m2.cols; i++) 
                for (let n = 0; n < this.cols; n++) 
                    array[m2.cols * j + i] += this.item(j, n) * m2.item(n, i);

        return new Matrix(this.rows, m2.cols, array);
    }

    multiply_vec(vec) {
        return this.multiply_mat(Matrix.from_vec(vec));
    }

    static from_vec(...vec) {
        let a = [];
        for (let row = 0; row < vec[0].length; row++) {
            for (let v of vec)
                a.push(v.components[row]);
        }
        
        return new Matrix(vec[0].length, vec.length, a);
    }

    // Returns a diagonal matrix with all 1's
    static identity(size) {
        let array = [];
        for (let row = 0; row < size; row++)
            for (let col = 0; col < size; col++)
                array.push((row == col) ? 1 : 0);

        return new Matrix(size, size, array);
    }

    // True means every single value of this matrix equals m2
    equals(m2) {
        if (this.rows != m2.rows || this.cols != m2.cols)
            return false;
        
        for (let i = 0; i < this.array.length; i++)
            // Sometimes we are comparing values like 0 and -5.5e-17 or 1 and 0.99999999999
            // which are effectively the same but floating point precision  (; 3 ;)
            if (Math.abs(this.array[i] - m2.array[i]) > 0.0000000000001) 
                return false
        return true;
    }

    to_string() {
        let s = "";
        for (let j = 0; j < this.rows; j++) {
            for (let i = 0; i < this.cols; i++)
                s += ((i == 0 && j == 0) ? " " : "  ") + this.item(j, i);
            if (j + 1 == this.rows)
                s += " ]"
            s += "\n"
        }
        return "[" + s;
    }

    __scratch_matrix(row, col) {
        let array = [];
        for (let j = 0; j < this.rows; j++)
            for (let i = 0; i < this.cols; i++) 
                if (!(i == col || j == row))
                    array.push(this.item(j, i));
        return new Matrix(this.rows - 1, this.cols - 1, array);
    }

    // Determinant
    det() {
        if (this.rows != this.cols)
            throw `Unable to find determinant of non-square ${this.rows}x${this.cols} matrix`;
        if (this.rows == 1)
            return this.array[0];
        
        let det = 0;
        for (let i = 0; i < this.cols; i++) 
            det += ((i % 2 == 0) ? 1 : -1) * this.item(0, i) * this.__scratch_matrix(0, i).det();
        
        return det;
    }

    transpose() {
        if (this.rows != this.cols)
            throw `Unable to transpose non-square ${this.rows}x${this.cols} matrix: `;

        let copy = this.copy();
        for (let j = 0; j < this.rows; j++) 
            for (let i = j + 1; i < this.cols; i++) {
                // Swap (x, y) with (y, x)
                let temp = copy.item(j, i);
                copy.set(j, i, copy.item(i, j));
                copy.set(i, j, temp);
            }

        return copy;
    }
        
    inverse() {
        // Using this algorithm
        // https://www.mathsisfun.com/algebra/matrix-inverse-minors-cofactors-adjugate.html
        if (this.rows != this.cols)
            throw `Unable to find inverse of non-square ${this.rows}x${this.cols} matrix`;

        let det = this.det();
        if (det == 0)
            throw `Unable to find inverse of 0 determinant matrix`;
            
        let mat = [];
        // Matrix of minors
        for (let j = 0; j < this.rows; j++) 
            for (let i = 0; i < this.cols; i++) 
                mat.push(this.__scratch_matrix(j, i).det());

        // Cofactors
        let flip = -1;
        for (let i = 0; i < mat.length; i++) {
            if (i % this.cols == 0)
                flip *= -1

            mat[i] *= (i % 2 == 0) ? flip : -flip;
        }

        // Adjugate
        let m = new Matrix(this.rows, this.cols, mat);
        m = m.transpose();

        return m.multiply_scalar(1 / det);
    }

    to_vec() {
        return new Vec(this.array);
    }
}

// Solves linear systems
function solve_sys_equation(equations) {
    // Each equation is an array of coefficents [a, b, c, ... , last_element]
    // Representing the equation ax + by + cz + ... = last_element
    // Where the last_element's coefficient is 1. i.e is a constant
    // equations = [[a, b, c, ...], [...], ...]
    
    if (equations.length != (equations[0].length - 1))
        throw `Number of equations less than number of unknowns`;

    // Gaussian elimination algorithm
    // https://www.youtube.com/watch?v=qhF59G5OTng
    // First we need to reorder the equations such that there are no 0's
    // on the diagonal; i.e. for all n >=0 equations[n][n] != 0
    // To do this we swap rows if we can

    // Lists the possible rows which each equation can be
    let row_choices = [];
    for (let i = 0; i < equations.length; i++) {
        let choices_row_i = [];
        // Sometimes we have a value like 6.17e-17 which is 0 but floating imprecision
        // cucked us, therefore we instead of checking for 0's on the diagonal we just
        // check if the value is very very smol, i.e. value ~ 0
        for (let j = 0; j < equations.length; j++)
            if (Math.abs(equations[j][i]) > 0.00000000001) // != 0
                choices_row_i.push(j)

        if (choices_row_i.length == 0)
            throw `No unique solution exists because column ${i} is all 0s`;
        row_choices.push(choices_row_i);
    }

    let row_order = [];
    function contains(array, element) {
        for (let item of array)
            if (element == item)
                return true;
        return false;
    }

    function choose_row(row_index) {
        if (row_index >= equations.length)
            return true

        let possible_choices = row_choices[row_index];
        let index = 0;
        do {
            let choice = possible_choices[index];
            if (!contains(row_order, choice)) {
                row_order.push(choice);

                let success = choose_row(row_index + 1);
                if (success)
                    return true;
                else {
                    row_order.pop();
                }
            }
            index += 1;
        } while (index < possible_choices.length);

        return false;
    }
    choose_row(0);

    
    // Test our row swap algorithm
    // console.log(row_order);
    for (let i = 0; i < row_order.length; i++)
        if (equations[row_order[i]][i] == 0)
            throw `Row swap algorithm failed`;

    
    // Gaussian elimination algorithm begins
    // https://www.youtube.com/watch?v=qhF59G5OTng
    for (let i = 0; i < equations.length; i++) {
        // Select pivot element and divide entire row by it
        let row_i = equations[row_order[i]];
        let pivot = row_i[i];

        for (let j = 0; j < row_i.length; j++)
            row_i[j] /= pivot;
        
        // for every row below the pivot row, add (-1 * M * pivot_row)
        // Where M is the ith element of the row
        for (let j = i + 1; j < equations.length; j++) {
            let row_j = equations[row_order[j]];
            let M = row_j[i];

            for (let k = 0; k < row_j.length; k++)
                row_j[k] -= M * row_i[k];
        }
    }

    // We should be in row echleon form now
    
    // Init empty solution vector
    let solution = [];
    for (let i = 0; i < equations.length; i++)
        solution.push(0);

    for (let i = row_order.length - 1; i >= 0; i--) {
        let row_i = equations[row_order[i]];
        
        for (let j = i + 1; j < equations.length; j++)
            row_i[row_i.length - 1] -= row_i[j] * solution[j];

        solution[i] = row_i[row_i.length - 1] / row_i[i];
    }
    
    return new Vec(solution);
}


//////////////////////// HTML stuff to make page nice ///////////////////////////////////


var nerd_shit = document.getElementById("what_a_nerd");
var nerd_button = document.getElementById("nerd_button");
nerd_button.addEventListener("click", () => {
    if (nerd_shit.style.display == "none") {
        nerd_shit.style.display = "block";
        nerd_button.innerHTML = "Ha, what a nerd! (Hide)";
        setTimeout(() => {
            nerd_shit.scrollIntoView(true);
        }, 10);
    }
    else {
        nerd_shit.style.display = "none";
        nerd_button.innerHTML = "Show me the math!";
    }
});


//////////////////////// Pendulum below ////////////////////////////////

var trace_path = false;
var random_start = true;

var x_s = canvas_width / 2;
var y_s = 200;

var g = 9.81;
var N = 4;
var draw_radius = canvas_width / 2.5 / N;
var L_n = [];
var omega_n = [];
var theta_n = [];
var mass_n = [];

var delta_t = 0.003;
var time_end = 45;
var num_steps = time_end / delta_t;
var current_step = 0;

function setup_initial_conditions() {
    draw_radius = canvas_width / 2.5 / N;
    L_n = [];
    omega_n = [];
    theta_n = [];
    mass_n = [];

    console.log("Is random: " + random_start);
    for (let n = 0; n < N; n++) {
        L_n.push(1);
        omega_n.push(0);
        mass_n.push(1);

        if (random_start)
            theta_n.push(Math.PI * 2 * Math.random());
        else
            theta_n.push(Math.PI / 2);
    }
}
setup_initial_conditions();

var trace_path_btn = document.getElementById("pathTraceButton");
var reset_btn = document.getElementById("resetButton");
var increase_btn = document.getElementById("increaseButton");
var decrease_btn = document.getElementById("decreaseButton");
var random_start_btn = document.getElementById("randomButton");

trace_path_btn.addEventListener("click", () => {
    trace_path = !trace_path;
    p5.clear();
});
reset_btn.addEventListener("click", () => {
    current_step = 0;
    p5.clear();
});
random_start_btn.addEventListener("click", () => {
    random_start = !random_start;
    if (random_start)
        random_start_btn.innerHTML = "Change to simple start";
    else
        random_start_btn.innerHTML = "Change to random start";
    restart_animation();
});
increase_btn.addEventListener("click", () => {
    change_N(1);
    restart_animation();
});
decrease_btn.addEventListener("click", () => {
    if (change_N(-1))
    restart_animation();
});
function change_N(delta_N) {
    if (N + delta_N < 1)
        return false;
    N += delta_N;
    return true;
}

var progress_container = document.getElementById("progressContainer");
var progress_bar = document.getElementById("progressBar");
var progress_text = document.getElementById("progressText");

function set_progress(percentage) {
    function zero_pad(number, max_length) {
        number = number.toString();
        while (number.length < max_length)
            number += "0";
        return number;
    }
    progress_container.style.display = "block";
    // percentage should be between 0 and 1.0
    progress_bar.value = progress_bar.max * percentage;
    progress_text.innerHTML = zero_pad((100 * percentage).toPrecision(4), 5) + " %";
}

function get_points() {
    let start = Date.now();
    var points = [];
    let alpha_index = 0;
    let a_x_index = alpha_index + N;
    let a_y_index = a_x_index + N;
    let tension_index = a_y_index + N;
    let constant = tension_index + N;

    function heavyLoad() {
        let equations = [];

        for (let n = 0; n < N; n++) {
            let eqs = [];
            for (let j = 0; j < 4; j++) {
                let eq = [];
                for (let i = 0; i < 4 * N + 1; i++)
                    eq.push(0);
                eqs.push(eq);
            }
    
            eqs[0][a_x_index + n] = 1;
            if (n > 0) eqs[0][a_x_index + n - 1] = -1;
            eqs[0][alpha_index + n] = -L_n[n] * Math.cos(theta_n[n]);
            eqs[0][constant] = -L_n[n] * Math.pow(omega_n[n], 2) * Math.sin(theta_n[n]);
            
            eqs[1][a_y_index + n] = 1;
            if (n > 0) eqs[1][a_y_index + n - 1] = -1;
            eqs[1][alpha_index + n] = L_n[n] * Math.sin(theta_n[n]);
            eqs[1][constant] = -1 * L_n[n] * Math.pow(omega_n[n], 2) * Math.cos(theta_n[n]);
            
            eqs[2][a_x_index + n] = mass_n[n];
            eqs[2][tension_index + n] = Math.sin(theta_n[n]);
            if (n + 1 < N) eqs[2][tension_index + n + 1] = -1 * Math.sin(theta_n[n + 1]);
            
            eqs[3][a_y_index + n] = mass_n[n];
            eqs[3][tension_index + n] = Math.cos(theta_n[n]);
            if (n + 1 < N) eqs[3][tension_index + n + 1] = -1 * Math.cos(theta_n[n + 1]);
            eqs[3][constant] = mass_n[n] * g;
    
            equations.push(...eqs);
        }
    
        // console.log(equations);

        let result = solve_sys_equation(equations);
    
        let alpha_n = [];
        for (let i = alpha_index; i < alpha_index + N; i++) 
            alpha_n.push(result.components[i]);
    
        // console.log(...alpha_n);
        
        let x_n = 0;
        let y_n = 0;
        for (let n = 0; n < N; n++) {
            omega_n[n] += delta_t * alpha_n[n];
            theta_n[n] += delta_t * omega_n[n];
            x_n += L_n[n] * Math.sin(theta_n[n]);
            y_n += L_n[n] * Math.cos(theta_n[n]);
            
            points.push(x_n, y_n);
        }
    }


    for (let i = 0; i < num_steps; i++) {
        setTimeout(() => {
            heavyLoad();
        }, 0);

        setTimeout(() => {
            set_progress(i / num_steps);
        }, 0);
    }

    console.log(Date.now() - start + " ms to compute animation");

    return points;
}

var animation = null;
var animation_playing = false;

function restart_animation() {
    stop_animation();
    setup_initial_conditions();
    start_animation();
}

function stop_animation() {
    if (animation_playing) {
        clearInterval(animation);
        animation_playing = false;
        
        current_step = 0;
        p5.clear();
    }
}

function start_animation() {
    stop_animation();
    
    let points = get_points();
    setTimeout(() => {
        progress_container.style.display = "none";
    }, 0);
    
    animation = setInterval(() => {
        if (current_step >= num_steps)
            stop_animation();

        animation_playing = true;

        if (!trace_path)
            p5.clear();

        let start_index = current_step * N * 2;
        for (let n = 0; n < N; n++) {
            let x_n_1 = 0;
            let y_n_1 = 0;
            if (n > 0) {
                x_n_1 = points[start_index + n * 2 - 2];
                y_n_1 = points[start_index + n * 2 - 1];
            }
            let x_n = points[start_index + n * 2];
            let y_n = points[start_index + n * 2 + 1];
                
            if (trace_path && current_step > 0) {
                let last_start_index = (current_step - 1) * N * 2;
                let last_x_n_1 = 0;
                let last_y_n_1 = 0;
                if (n > 0) {
                    last_x_n_1 = points[last_start_index + n * 2 - 2];
                    last_y_n_1 = points[last_start_index + n * 2 - 1];
                }
                let last_x_n = points[last_start_index + n * 2];
                let last_y_n = points[last_start_index + n * 2 + 1];
                
                // Given 4 sets of points (x_n, y_n), (x_n-1, y_n-1), (last_x_n, last_y_n), (last_x_n-1, last_y_n-1) 
                // This forms a quadrilateral, drawn with two triangles
                p5.stroke(255);
                p5.fill(255);
                p5.triangle(x_s + draw_radius * x_n, y_s + draw_radius * y_n,
                    x_s + draw_radius * x_n_1,   y_s + draw_radius * y_n_1,
                    x_s + draw_radius * last_x_n,   y_s + draw_radius * last_y_n);
                p5.triangle(x_s + draw_radius * x_n, y_s + draw_radius * y_n,
                    x_s + draw_radius * last_x_n_1,   y_s + draw_radius * last_y_n_1,
                    x_s + draw_radius * last_x_n,   y_s + draw_radius * last_y_n);
                // p5.line(x_s + draw_radius * last_x_n_1, y_s + draw_radius * last_y_n_1,
                //     x_s + draw_radius * last_x_n,   y_s + draw_radius * last_y_n);
            }

            p5.stroke(100);
            if (trace_path)
                p5.stroke(150);
            p5.line(x_s + draw_radius * x_n_1, y_s + draw_radius * y_n_1,
                x_s + draw_radius * x_n,   y_s + draw_radius * y_n);

            p5.stroke(255, 0, 0);
            p5.fill(255, 0, 0);
            p5.circle(x_s + draw_radius * x_n, y_s + draw_radius * y_n, 2);

            // End for loop
        }
            
        current_step = (current_step + 1) % num_steps;
    }, 1);
}

console.log("pendulum.js loaded");
start_animation();
}

</script>

<style scoped>

</style>
